import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactElement, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { PoolProductAutocompleteList } from '../autocomplete-list/product-autocomplete-list/pool-product-autocomplete-list';
import { Product } from '../../models/product';
import { bestLocaleSelector } from '../../selectors/locale-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';

export interface PaloAddPositionsDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called once per article, in the order they were picked. */
    onSelect: (product: Product | null) => void;
    /** Restricts the search to the supplier the document is with. */
    supplierIds?: number[];
    includeNormArticles?: boolean;
    /** For the message when the supplier carries nothing that matches. */
    supplierName?: string;
    disabled?: boolean;
    /** No supplier chosen yet on a document that has one; see the row search. */
    supplierMissing?: boolean;
}

/** The article's name in the language the mask is shown in. */
const productLabel = (product: Product, locale: string): string => {
    const name = product.name;

    if ('string' === typeof name) {
        return name;
    }

    return (name as Record<string, string>)?.[locale] ?? String(product.id);
};

/**
 * Several articles into the document, without leaving the dialog.
 *
 * It is what ERPNext's "Add multiple" does, measured in the customer's instance
 * on 2026-09-28: a search, and every article picked becomes a row while the
 * dialog stays open for the next one. Ours leaves out its "Set Quantity" step —
 * the quantity stands in the row itself, one line further on, where ERPNext
 * asks because its grid can be collapsed (#2208).
 *
 * The search used to sit flat above the table, which was this dialog without a
 * lid: the same field, the same repetition, in the middle of the mask.
 */
export const PaloAddPositionsDialog = ({
    open,
    onClose,
    onSelect,
    supplierIds,
    includeNormArticles = false,
    supplierName,
    disabled,
    supplierMissing = false,
}: PaloAddPositionsDialogProps): ReactElement => {
    const locale = useAppSelector(bestLocaleSelector);
    const [added, setAdded] = useState<string[]>([]);

    const handleClose = (): void => {
        setAdded([]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth={true}>
            <DialogTitle sx={{ fontSize: '15px', fontWeight: 640 }}>
                <FormattedMessage id="app.positions.addMultiple" defaultMessage="Mehrere hinzufügen" />
            </DialogTitle>
            <DialogContent>
                {supplierMissing ? (
                    <Box sx={{ fontSize: '12.5px', color: tokens.color.ink3, padding: '6px 0 2px' }}>
                        <FormattedMessage
                            id="app.positions.chooseSupplierFirst"
                            defaultMessage="Bitte zuerst Lieferanten wählen"
                        />
                    </Box>
                ) : (
                    <PoolProductAutocompleteList
                        onSelected={product => {
                            if (!product) {
                                return;
                            }

                            onSelect(product);
                            setAdded(current => [...current, productLabel(product, locale)]);
                        }}
                        clearInputOnSelect={true}
                        label={<FormattedMessage id="app.label.product" />}
                        disabled={disabled}
                        supplierIds={supplierIds}
                        includeNormArticles={includeNormArticles}
                        emptyMessage={
                            supplierName ? (
                                <FormattedMessage
                                    id="app.positions.noArticlesOfSupplier"
                                    defaultMessage="Keine Artikel von {supplier} gefunden"
                                    values={{ supplier: supplierName }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="app.positions.noArticles"
                                    defaultMessage="Keine Artikel gefunden"
                                />
                            )
                        }
                    />
                )}

                {/* What has gone in so far, so one can pick on without going
                    back to the table to check. */}
                {added.length > 0 && (
                    <Box sx={{ mt: '14px', fontSize: '12.5px', color: tokens.color.ink2 }}>
                        <Box sx={{ color: tokens.color.ink3, mb: '4px' }}>
                            <FormattedMessage
                                id="app.positions.added"
                                values={{ value: added.length }}
                                defaultMessage="{value, plural, one {# Position hinzugefügt} other {# Positionen hinzugefügt}}"
                            />
                        </Box>
                        {added.map((name, index) => (
                            <Box key={index} sx={{ padding: '2px 0' }}>
                                {name}
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {/* Every pick is already a row, so leaving is leaving either
                    way. The quiet way out is there for anyone who opened the
                    dialog and found nothing to add. */}
                <Button size="small" variant="outlined" color="inherit" onClick={handleClose}>
                    <FormattedMessage id="app.common.close" defaultMessage="Schließen" />
                </Button>
                <Button size="small" variant="contained" color="primary" onClick={handleClose}>
                    <FormattedMessage id="app.common.done" defaultMessage="Fertig" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};
